import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client';
import { cn } from '../lib/cn';

/**
 * Unified Customer Record (UCR) — Phase 1.
 * Endpoints now live: tenants-get, get-subscription, get-credits, list-calls, get-transcript.
 * Remaining stubs: Calendar, Integrations, Tickets (EspoCRM Phase 2), Audit (M1.8).
 */

type TenantRecord = {
  clientId: string;
  accountNumber: string;
  businessName: string;
  industry: string | null;
  employeeCount: string | null;
  businessPhone: string | null;
  businessAddress: { street?: string | null; city?: string | null; state?: string | null; zip?: string | null } | null;
  website: string | null;
  timezone: string | null;
  planType: string;
  subscriptionStatus: string;
  stripeCustomerId: string | null;
  twilioPhoneNumber: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};
type UserSummary = {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  status: string;
  lastLoginAt: string | null;
};
type TenantDetail = { tenant: TenantRecord; users: UserSummary[] };

type Subscription = {
  stripeCustomerId: string;
  email: string | null;
  balance: number;
  currency: string | null;
  defaultPaymentMethod: string | null;
  subscription: {
    id: string;
    status: string;
    planType: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    trialEnd: string | null;
  } | null;
  invoices: Array<{
    id: string;
    amountDue: number;
    amountPaid: number;
    currency: string;
    status: string;
    created: string;
    hostedInvoiceUrl: string | null;
    number: string | null;
  }>;
};

type Credits = {
  planType: string;
  planQuota: number;
  used: number;
  remaining: number;
  creditBalance: number;
  percentUsed: number;
  currentPeriodEnd: string | null;
};

type CallSummary = {
  callId: string;
  startTime: string | null;
  endTime: string | null;
  duration: number | null;
  from: string | null;
  to: string | null;
  callStatus: string | null;
  outcome: string | null;
  direction: string | null;
  hasRecording: boolean;
  hasTranscript: boolean;
};
type CallList = { calls: CallSummary[]; nextCursor: string | null };

export function CustomerRecordPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const tenantQuery = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.get<TenantDetail>(`/tenants/${tenantId}`),
    enabled: !!tenantId,
  });
  const subQuery = useQuery({
    queryKey: ['tenant', tenantId, 'subscription'],
    queryFn: () => api.get<Subscription | null>(`/tenants/${tenantId}/subscription`),
    enabled: !!tenantId,
  });
  const creditsQuery = useQuery({
    queryKey: ['tenant', tenantId, 'credits'],
    queryFn: () => api.get<Credits>(`/tenants/${tenantId}/credits`),
    enabled: !!tenantId,
  });
  const callsQuery = useQuery({
    queryKey: ['tenant', tenantId, 'calls'],
    queryFn: () => api.get<CallList>(`/tenants/${tenantId}/calls?limit=20`),
    enabled: !!tenantId,
  });

  if (tenantQuery.isLoading) return <LoadingBlock tenantId={tenantId} />;
  if (tenantQuery.isError) return <ErrorBlock error={tenantQuery.error} />;
  if (!tenantQuery.data) return null;

  const { tenant, users } = tenantQuery.data;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">{tenant.businessName}</h1>
            <StatusBadge status={tenant.subscriptionStatus} />
            <Badge value={tenant.planType} />
          </div>
          <div className="text-xs text-muted mt-1 flex items-center gap-3">
            <span>Account <code className="font-mono">{tenant.accountNumber}</code></span>
            <span>·</span>
            <span>Tenant <code className="font-mono">{tenant.clientId}</code></span>
            {tenant.industry && (<><span>·</span><span>{tenant.industry}</span></>)}
          </div>
        </div>
        <Link to="/tenants" className="text-xs text-muted hover:text-accent">← Back to tenant list</Link>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <TenantInfoPanel tenant={tenant} />
        <UsersPanel users={users} />
        <SubscriptionPanel query={subQuery} />
        <CreditsPanel query={creditsQuery} />
        <RecentCallsPanel
          query={callsQuery}
          selectedCallId={selectedCallId}
          onSelect={setSelectedCallId}
        />
        <StubPanel title="Calendar" note="DynamoDB calendar-connections (Phase 2)" />
        <StubPanel title="Integrations" note="OpenDental / Healow (Phase 2)" />
        <StubPanel title="Tickets (EspoCRM)" note="Phase 2 — UCR ticket widget" />
        <AuditTrailPanel tenantId={tenantId!} />
      </div>

      {selectedCallId && tenantId && (
        <TranscriptDrawer
          tenantId={tenantId}
          callId={selectedCallId}
          onClose={() => setSelectedCallId(null)}
        />
      )}
    </div>
  );
}

// ─── Panels ────────────────────────────────────────────────────────────────

function TenantInfoPanel({ tenant }: { tenant: TenantRecord }) {
  const addr = tenant.businessAddress;
  const addrLine = addr ? [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ') : null;
  return (
    <div className="panel p-4 col-span-1">
      <div className="text-sm font-medium text-fg mb-3">Tenant Info</div>
      <dl className="space-y-2 text-xs">
        <Row label="Twilio #" value={tenant.twilioPhoneNumber} mono />
        <Row label="Business #" value={tenant.businessPhone} mono />
        <Row label="Website" value={tenant.website} />
        <Row label="Timezone" value={tenant.timezone} />
        <Row label="Employees" value={tenant.employeeCount} />
        <Row label="Address" value={addrLine} />
        <Row label="Stripe" value={tenant.stripeCustomerId} mono />
        <Row label="Created" value={formatDate(tenant.createdAt)} />
      </dl>
    </div>
  );
}

function UsersPanel({ users }: { users: UserSummary[] }) {
  return (
    <div className="panel p-4 col-span-2">
      <div className="text-sm font-medium text-fg mb-3">
        Users <span className="text-xs text-muted font-normal">({users.length})</span>
      </div>
      {users.length === 0 ? (
        <div className="text-xs text-muted">No users</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-muted uppercase tracking-wide">
            <tr><th className="text-left pb-1">Name / Email</th><th className="text-left pb-1">Role</th><th className="text-left pb-1">Status</th><th className="text-left pb-1">Last Login</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.userId}>
                <td className="py-1.5">
                  <div className="font-medium text-fg">{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</div>
                  <div className="text-muted font-mono">{u.email}</div>
                </td>
                <td className="py-1.5"><Badge value={u.role} /></td>
                <td className="py-1.5"><StatusBadge status={u.status} /></td>
                <td className="py-1.5 text-muted">{formatDate(u.lastLoginAt) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SubscriptionPanel({ query }: { query: ReturnType<typeof useQuery<Subscription | null>> }) {
  return (
    <div className="panel p-4">
      <div className="text-sm font-medium text-fg mb-3">Subscription</div>
      {query.isLoading && <div className="text-xs text-muted">Loading…</div>}
      {query.isError && <div className="text-xs text-danger">failed to load</div>}
      {query.data === null && <div className="text-xs text-muted">No Stripe customer (legacy / trial)</div>}
      {query.data && (
        <dl className="space-y-2 text-xs">
          {query.data.subscription ? (
            <>
              <Row label="Status" value={<StatusBadge status={query.data.subscription.status} />} />
              <Row label="Plan" value={<Badge value={query.data.subscription.planType ?? '—'} />} />
              <Row label="Renews" value={formatDate(query.data.subscription.currentPeriodEnd)} />
              {query.data.subscription.cancelAtPeriodEnd && (
                <Row label="⚠" value={<span className="text-danger">Set to cancel at period end</span>} />
              )}
              {query.data.subscription.trialEnd && (
                <Row label="Trial ends" value={formatDate(query.data.subscription.trialEnd)} />
              )}
            </>
          ) : (
            <div className="text-xs text-muted">No active subscription</div>
          )}
          <Row label="Balance" value={formatMoney(query.data.balance, query.data.currency)} />
          <div className="pt-2 border-t border-border">
            <div className="text-muted mb-1">Recent invoices ({query.data.invoices.length})</div>
            <div className="space-y-1">
              {query.data.invoices.slice(0, 5).map((inv) => (
                <div key={inv.id} className="flex justify-between">
                  <span className="text-muted">{formatDate(inv.created)}</span>
                  <span className="font-mono">{formatMoney(inv.amountPaid, inv.currency)}</span>
                  <span className={cn(
                    inv.status === 'paid' ? 'text-accent' : inv.status === 'open' ? 'text-danger' : 'text-muted',
                  )}>{inv.status}</span>
                </div>
              ))}
            </div>
          </div>
        </dl>
      )}
    </div>
  );
}

function CreditsPanel({ query }: { query: ReturnType<typeof useQuery<Credits>> }) {
  return (
    <div className="panel p-4">
      <div className="text-sm font-medium text-fg mb-3">Credits & Usage</div>
      {query.isLoading && <div className="text-xs text-muted">Loading…</div>}
      {query.isError && <div className="text-xs text-danger">failed to load</div>}
      {query.data && (
        <div className="space-y-3 text-xs">
          <div>
            <div className="flex justify-between text-fg mb-1">
              <span>{query.data.used} / {query.data.planQuota} calls</span>
              <span className="text-muted">{query.data.percentUsed}%</span>
            </div>
            <div className="w-full bg-border/40 rounded h-2 overflow-hidden">
              <div
                className={cn(
                  'h-full',
                  query.data.percentUsed >= 90 ? 'bg-danger' : 'bg-accent',
                )}
                style={{ width: `${query.data.percentUsed}%` }}
              />
            </div>
          </div>
          <dl className="space-y-2">
            <Row label="Plan" value={<Badge value={query.data.planType} />} />
            <Row label="Remaining" value={`${query.data.remaining} calls`} />
            <Row label="Credit balance" value={`${query.data.creditBalance}`} />
            <Row label="Period ends" value={formatDate(query.data.currentPeriodEnd)} />
          </dl>
        </div>
      )}
    </div>
  );
}

function RecentCallsPanel({
  query,
  selectedCallId,
  onSelect,
}: {
  query: ReturnType<typeof useQuery<CallList>>;
  selectedCallId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div className="panel p-4 col-span-3">
      <div className="text-sm font-medium text-fg mb-3">
        Recent Calls <span className="text-xs text-muted font-normal">(last 20)</span>
      </div>
      {query.isLoading && <div className="text-xs text-muted">Loading…</div>}
      {query.isError && <div className="text-xs text-danger">failed to load</div>}
      {query.data && query.data.calls.length === 0 && <div className="text-xs text-muted">No calls</div>}
      {query.data && query.data.calls.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-muted uppercase tracking-wide">
            <tr>
              <th className="text-left pb-1 font-medium">Start</th>
              <th className="text-left pb-1 font-medium">From</th>
              <th className="text-left pb-1 font-medium">Status</th>
              <th className="text-left pb-1 font-medium">Outcome</th>
              <th className="text-left pb-1 font-medium">Duration</th>
              <th className="text-left pb-1 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {query.data.calls.map((c) => (
              <tr key={c.callId} className="hover:bg-bg/60">
                <td className="py-1.5 text-muted">{formatDate(c.startTime)}</td>
                <td className="py-1.5 font-mono">{c.from ?? '—'}</td>
                <td className="py-1.5"><StatusBadge status={c.callStatus ?? 'unknown'} /></td>
                <td className="py-1.5">{c.outcome ?? <span className="text-muted">—</span>}</td>
                <td className="py-1.5 text-muted">{c.duration ? `${c.duration}s` : '—'}</td>
                <td className="py-1.5">
                  {(c.hasRecording || c.hasTranscript) && (
                    <button
                      onClick={() => onSelect(c.callId === selectedCallId ? null : c.callId)}
                      className="text-accent hover:underline"
                    >
                      {c.callId === selectedCallId ? 'close' : 'view'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Transcript drawer ─────────────────────────────────────────────────────

type TranscriptResponse = {
  call: Record<string, unknown>;
  audioUrl: string | null;
  transcript: string | Record<string, unknown> | null;
};

function TranscriptDrawer({ tenantId, callId, onClose }: { tenantId: string; callId: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['tenant', tenantId, 'call', callId, 'transcript'],
    queryFn: () => api.get<TranscriptResponse>(`/tenants/${tenantId}/calls/${callId}/transcript`),
  });

  return (
    <div className="fixed inset-0 bg-fg/20 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-surface border border-border rounded-md w-[720px] max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Call Transcript</div>
            <code className="text-xs text-muted">{callId}</code>
          </div>
          <button onClick={onClose} className="text-muted hover:text-fg text-sm">✕</button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3 text-xs">
          {query.isLoading && <div className="text-muted">Loading…</div>}
          {query.isError && <div className="text-danger">Failed to load</div>}
          {query.data && (
            <>
              {query.data.audioUrl && (
                <audio controls src={query.data.audioUrl} className="w-full">
                  Your browser does not support the audio element.
                </audio>
              )}
              <pre className="font-mono whitespace-pre-wrap bg-bg p-3 rounded border border-border text-xs">
                {typeof query.data.transcript === 'string'
                  ? query.data.transcript
                  : query.data.transcript
                  ? JSON.stringify(query.data.transcript, null, 2)
                  : 'No transcript available'}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared UI ─────────────────────────────────────────────────────────────

function Row({ label, value, mono }: { label: string; value: unknown; mono?: boolean }) {
  const display =
    value == null || value === '' ? (
      <span className="text-muted">—</span>
    ) : typeof value === 'string' ? (
      value
    ) : (
      (value as React.ReactNode)
    );
  return (
    <div className="flex gap-3">
      <dt className="text-muted w-24 flex-shrink-0">{label}</dt>
      <dd className={cn('text-fg flex-1 break-words', mono && 'font-mono')}>{display}</dd>
    </div>
  );
}
function Badge({ value }: { value: string }) { return <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-border/60 text-fg">{value}</span>; }
function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'active' || status === 'paid' || status === 'completed'
      ? 'bg-accent/10 text-accent'
      : status === 'past_due' || status === 'failed'
      ? 'bg-danger/10 text-danger'
      : status === 'cancelled' || status === 'suspended' || status === 'canceled' || status === 'no-answer' || status === 'busy'
      ? 'bg-muted/10 text-muted'
      : 'bg-border/60 text-fg';
  return <span className={cn('inline-block text-xs px-1.5 py-0.5 rounded', tone)}>{status}</span>;
}
function StubPanel({ title, note }: { title: string; note: string }) {
  return (
    <div className="panel p-4">
      <div className="text-sm font-medium text-fg">{title}</div>
      <div className="text-xs text-muted mt-1">Pending wiring</div>
      <code className="block text-xs mt-2 text-muted">{note}</code>
    </div>
  );
}

type AuditRowSummary = {
  auditId: string;
  timestamp: string;
  actorEmail: string;
  actorRole: string;
  action: string;
  reason: string | null;
  success: boolean;
};
type AuditListResponse = { rows: AuditRowSummary[]; count: number };

function AuditTrailPanel({ tenantId }: { tenantId: string }) {
  // Default to last 30 days — tenant-level audit trails are sparser than actor-level
  // so a wider range is more useful in the compact panel view.
  const today = new Date();
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - 29);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const params = `tenant=${encodeURIComponent(tenantId)}&from=${fmt(from)}&to=${fmt(today)}&limit=20`;

  const q = useQuery({
    queryKey: ['tenant', tenantId, 'audit'],
    queryFn: () => api.get<AuditListResponse>(`/audit?${params}`),
  });

  return (
    <div className="panel p-4 col-span-3">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-sm font-medium text-fg">
          Audit Trail <span className="text-xs text-muted font-normal">(last 30 days, up to 20 rows)</span>
        </div>
        <Link
          to={`/audit?tenant=${encodeURIComponent(tenantId)}`}
          className="text-xs text-accent hover:underline"
        >
          Open full log →
        </Link>
      </div>
      {q.isLoading && <div className="text-xs text-muted">Loading…</div>}
      {q.isError && <div className="text-xs text-danger">failed to load</div>}
      {q.data && q.data.rows.length === 0 && (
        <div className="text-xs text-muted">No admin actions on this tenant in the last 30 days.</div>
      )}
      {q.data && q.data.rows.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-muted uppercase tracking-wide">
            <tr>
              <th className="text-left pb-1 font-medium">When</th>
              <th className="text-left pb-1 font-medium">Who</th>
              <th className="text-left pb-1 font-medium">Action</th>
              <th className="text-left pb-1 font-medium">Outcome</th>
              <th className="text-left pb-1 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {q.data.rows.map((r) => (
              <tr key={r.auditId} className="hover:bg-bg/60">
                <td className="py-1.5 text-muted whitespace-nowrap">{formatDate(r.timestamp)}</td>
                <td className="py-1.5">
                  <div className="text-fg">{r.actorEmail}</div>
                  <div className="text-muted">{r.actorRole}</div>
                </td>
                <td className="py-1.5"><code className="font-mono">{r.action}</code></td>
                <td className="py-1.5">
                  <span className={cn(
                    'inline-block text-xs px-1.5 py-0.5 rounded',
                    r.success ? 'bg-accent/10 text-accent' : 'bg-danger/10 text-danger',
                  )}>
                    {r.success ? 'ok' : 'fail'}
                  </span>
                </td>
                <td className="py-1.5 text-muted max-w-xs truncate">{r.reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}
function formatMoney(cents: number, currency: string | null): string {
  const amount = cents / 100;
  const cur = currency?.toUpperCase() ?? 'USD';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(amount);
}
function LoadingBlock({ tenantId }: { tenantId?: string }) {
  return <div className="panel p-8 text-center text-sm text-muted">Loading tenant <code>{tenantId}</code>…</div>;
}
function ErrorBlock({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? `${error.status} · ${error.code} · ${error.message}` : String(error);
  return (
    <div className="panel p-4 text-sm text-danger bg-danger/5 border-danger/30">
      <div className="font-medium">Failed to load tenant</div>
      <div className="text-xs mt-1 font-mono">{msg}</div>
      <Link to="/tenants" className="text-xs underline mt-2 inline-block">← Back</Link>
    </div>
  );
}
